import Box from '@mui/material/Box';
import { useTheme } from '../../../../theme';
import { ButtonType } from '../../../foundations/buttons/button';
import { BodyM, Heading } from '../../../../typography';
import { type NotificationLevel } from '../../../foundations/status';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '../../../foundations/modal';
import { Status } from '../../../foundations/status';

import { useId, type JSX, type ReactNode } from 'react';

type AlertModalButtonProps = {
  text: string;
  onClick?: () => void | Promise<void>;
  isLoading?: boolean;
  status?: Exclude<NotificationLevel, 'loader' | 'spinner'>;
  type?: ButtonType;
};

export type AlertModalProps = {
  isOpen: boolean;
  title: string;
  description: ReactNode;
  primaryButton: AlertModalButtonProps;
  secondaryButton?: AlertModalButtonProps;
  onClose?: () => void;
  statusIcon: Exclude<NotificationLevel, 'loader'>;
};

export const AlertModal = ({
  isOpen,
  title,
  statusIcon,
  description,
  primaryButton,
  secondaryButton,
  onClose,
}: AlertModalProps): JSX.Element | null => {
  const theme = useTheme();
  const titleId = useId();

  return (
    <Modal isOpen={isOpen} role='alertdialog' aria-labelledby={titleId}>
      <ModalContent>
        <ModalHeader onClose={onClose} />

        <ModalBody>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              alignSelf: 'stretch',
            }}
          >
            <Status statusLevel={statusIcon} size={40} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                alignSelf: 'stretch',
              }}
            >
              <Heading
                id={titleId}
                tag='h1'
                variant='titleH4'
                fontWeight='medium'
                color={theme.text.generic.primary}
                css={{ margin: 0 }}
              >
                {title}
              </Heading>
              <BodyM
                color={theme.text.generic.secondary}
                css={{ textAlign: 'center' }}
              >
                {description}
              </BodyM>
            </Box>
          </Box>
        </ModalBody>

        <ModalFooter
          layout='equal-buttons'
          submitButton={{
            children: primaryButton.text,
            onClick: primaryButton.onClick,
            type: primaryButton.type ?? 'primary',
            isLoading: primaryButton.isLoading,
          }}
          secondaryButton={
            secondaryButton
              ? {
                  children: secondaryButton.text,
                  onClick: secondaryButton.onClick,
                  type: secondaryButton.type ?? 'secondary',
                  isLoading: secondaryButton.isLoading,
                }
              : undefined
          }
        />
      </ModalContent>
    </Modal>
  );
};
